/**
 * TraineeDashboardDialog — quick per-trainee deep-dive dialog opened from
 * the Trainees table. Shows attendance summary, last 30 days mini chart,
 * and per-assessment scores.
 */
import { useEffect, useRef, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import NotesTimeline from './NotesTimeline';
import TrainingHistoryCard from './TrainingHistoryCard';
import { useAuth } from '../../contexts/AuthContext';

const ALERT_COLOR = {
  Red: 'bg-rose-100 text-rose-700 border-rose-300',
  Amber: 'bg-amber-100 text-amber-700 border-amber-300',
  Green: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

const STATUS_LABELS = {
  present: 'Present', absent: 'Absent', leave: 'Leave', half_day: 'Half-Day',
  holiday: 'Holiday', week_off: 'Week-Off', not_joined: 'Not Joined',
};

export default function TraineeDashboardDialog({ trainee, onClose, onChanged, focus = null }) {
  const { get } = useApi();
  const { hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUnattempted, setShowUnattempted] = useState(false);
  const assessRef = useRef(null);

  useEffect(() => {
    if (!trainee) { setData(null); setShowUnattempted(false); return; }
    setLoading(true);
    (async () => {
      try {
        const res = await get(`/trainees/dashboard/trainees/${trainee.trainee_id}`);
        setData(res);
      } catch { /* tolerated */ }
      finally { setLoading(false); }
    })();
  }, [trainee, get]);

  useEffect(() => {
    if (data && focus === 'assessments') {
      setTimeout(() => assessRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [data, focus]);

  const attempted = (data?.assessments || []).filter(a => a.score != null);
  const unattempted = (data?.assessments || []).filter(a => a.score == null);
  const visibleAssessments = showUnattempted ? [...attempted, ...unattempted] : attempted;

  return (
    <Dialog open={!!trainee} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto"
                     data-testid="trainee-dashboard-dialog">
        {!trainee ? null : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <span>{trainee.name}</span>
                {data?.fields?.alert_status && (
                  <Badge className={ALERT_COLOR[data.fields.alert_status] || ''}
                         variant="outline">
                    {data.fields.alert_status}
                  </Badge>
                )}
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {trainee.employee_id ? `${trainee.employee_id} · ` : ''}
                {data?.batch_name || '—'}
                {data?.email ? ` · ${data.email}` : ''}
              </p>
            </DialogHeader>

            {loading || !data ? (
              <p className="p-6 text-center text-sm text-muted-foreground">Loading…</p>
            ) : (
              <div className="space-y-4">
                {/* KPI row */}
                <div className="grid grid-cols-3 gap-3">
                  <Card className="p-3">
                    <p className="text-xs text-muted-foreground">Attendance</p>
                    <p className={`font-display tabular-nums text-2xl font-bold ${
                      data.attendance_pct < 80 && data.attendance_total_days > 0 ? 'text-rose-600' : ''
                    }`}>
                      {data.attendance_total_days > 0 ? `${data.attendance_pct}%` : '—'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {data.attendance_worked}/{data.attendance_total_days} days
                    </p>
                  </Card>
                  <Card className="p-3">
                    <p className="text-xs text-muted-foreground">Assessment %</p>
                    <p className="font-display tabular-nums text-2xl font-bold">
                      {data.fields?.assessment != null ? `${data.fields.assessment}%` : '—'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {data.assessments.filter(a => a.passed).length}/
                      {data.assessments.filter(a => a.passed !== null).length} passed
                    </p>
                  </Card>
                  <Card className="p-3">
                    <p className="text-xs text-muted-foreground">Training Status</p>
                    <p className="font-display text-lg font-semibold truncate">
                      {data.fields?.training_status || '—'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      Cert: {data.fields?.certification_status || '—'}
                    </p>
                  </Card>
                </div>

                {/* 30-day mini chart */}
                <Card className="p-3" data-testid="trainee-30d-chart">
                  <p className="text-xs text-muted-foreground mb-1">Last 30 days</p>
                  <ResponsiveContainer width="100%" height={120}>
                    <AreaChart data={data.last_30d_attendance}>
                      <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={d => d.slice(5)} fontSize={10} />
                      <YAxis hide domain={[0, 100]} />
                      <Tooltip />
                      <Area type="step" dataKey="value" stroke="#1A6FF7" fill="#1A6FF7" fillOpacity={0.25} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>

                {/* Status breakdown */}
                <div className="grid grid-cols-7 gap-2 text-center">
                  {Object.entries(data.attendance_status_counts).map(([k, v]) => (
                    <Card key={k} className="p-2">
                      <p className="text-[10px] text-muted-foreground">{STATUS_LABELS[k] || k}</p>
                      <p className="font-display tabular-nums text-lg font-bold">{v}</p>
                    </Card>
                  ))}
                </div>

                {/* Assessments */}
                <Card ref={assessRef} data-testid="trainee-assessments-card">
                  <div className="p-3 border-b flex items-center justify-between gap-3">
                    <h4 className="font-display text-sm font-semibold">
                      Assessments
                      <span className="ml-2 text-xs font-normal text-muted-foreground tabular-nums"
                            data-testid="trainee-assessments-count">
                        {attempted.length} attempted
                      </span>
                    </h4>
                    {unattempted.length > 0 && (
                      <button type="button" onClick={() => setShowUnattempted(v => !v)}
                              className="text-xs text-muted-foreground underline-offset-2 hover:underline hover:text-foreground"
                              data-testid="toggle-unattempted-btn">
                        {showUnattempted ? 'Hide' : 'Show'} {unattempted.length} not scored
                      </button>
                    )}
                  </div>
                  {data.assessments.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">No assessments configured.</p>
                  ) : visibleAssessments.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground" data-testid="no-attempted-note">
                      No assessments attempted yet.
                    </p>
                  ) : (
                    <div className="divide-y">
                      {visibleAssessments.map(a => (
                        <div key={a.assessment_id} className="flex items-center justify-between p-3">
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="font-medium text-sm">{a.name}</p>
                              <p className="text-xs text-muted-foreground">
                                Pass mark {a.passing_marks} / {a.max_score}
                                {a.attempt_count > 1 && ` · attempt #${a.attempt_count}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {a.score != null ? (
                              <>
                                <div className="text-right">
                                  <p className="font-display tabular-nums text-lg font-bold">
                                    {a.score}<span className="text-xs text-muted-foreground">/{a.max_score}</span>
                                  </p>
                                </div>
                                {a.passed ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300"
                                         variant="outline">
                                    <CheckCircle2 className="w-3 h-3 mr-1" /> Pass
                                  </Badge>
                                ) : (
                                  <Badge className="bg-rose-100 text-rose-700 border-rose-300"
                                         variant="outline">
                                    <XCircle className="w-3 h-3 mr-1" /> Fail
                                  </Badge>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground">Not scored</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                {/* Training history — every batch this person attended */}
                <TrainingHistoryCard traineeId={trainee.trainee_id} traineeName={trainee.name} canEdit={hasRole('trainer')}
                                     onChanged={onChanged} />

                {/* Notes timeline (Phase 2C) */}
                <NotesTimeline traineeId={trainee.trainee_id} />
              </div>
            )}

            <DialogFooter>
              <Button onClick={onClose} data-testid="close-trainee-dashboard">Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
